import { useState } from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { Users, X } from "lucide-react";
import { create } from "zustand";
import { SHARED_FOLDER_COLORS, type SharedFolderColor } from "@hydra-notes/shared";
import { sharedFoldersApi, sharedAccent, useSharedFolders } from "@/data/sharedFolders";
import { useExpanded } from "@/store/expanded";
import { Button } from "@/ui/Button";
import { Input } from "@/ui/Input";
import { IconButton } from "@/ui/IconButton";
import { toast } from "@/ui/toast";
import s from "./SignInDialog.module.css";
import sf from "./SharedFolder.module.css";

interface CreateSharedFolderStore {
  open: boolean;
  setOpen: (open: boolean) => void;
}
export const useCreateSharedFolderDialog = create<CreateSharedFolderStore>((set) => ({
  open: false,
  setOpen: (open) => set({ open }),
}));

export function CreateSharedFolderDialog() {
  const { open, setOpen } = useCreateSharedFolderDialog();
  const [title, setTitle] = useState("");
  const [colorKey, setColorKey] = useState<SharedFolderColor>("teal");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    const name = title.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      const { folder } = await sharedFoldersApi.create(name, colorKey);
      await useSharedFolders.getState().refresh();
      useExpanded.getState().expand(`sf:${folder.id}`);
      toast.success(`"${folder.title}" created. Invite people from its folder settings.`);
      setOpen(false);
      setTitle("");
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={setOpen}>
      <Dialog.Portal>
        <Dialog.Overlay className={s.overlay} />
        <Dialog.Content className={s.dialog}>
          <div className={s.header}>
            <Users size={20} />
            <Dialog.Title className={s.title}>New shared folder</Dialog.Title>
          </div>
          <Dialog.Description className={s.subtitle}>
            A shared folder syncs live between everyone you invite. Its contents count against every
            member's storage.
          </Dialog.Description>
          <div className={s.form}>
            <Input
              placeholder="Folder name"
              value={title}
              autoFocus
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && void submit()}
            />
            <div className={sf.swatches}>
              {SHARED_FOLDER_COLORS.map((key) => (
                <button
                  key={key}
                  type="button"
                  title={key}
                  className={[sf.swatch, key === colorKey ? sf.swatchActive : ""].join(" ")}
                  style={{ "--swatch": sharedAccent(key) } as React.CSSProperties}
                  onClick={() => setColorKey(key)}
                />
              ))}
            </div>
            <Button variant="solid" disabled={!title.trim() || busy} onClick={() => void submit()}>
              {busy ? "Creating..." : "Create shared folder"}
            </Button>
          </div>
          <Dialog.Close asChild>
            <IconButton label="Close" className={s.close}>
              <X size={14} />
            </IconButton>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
